import React from 'react';
import { Box, Button, Paper, Stack, Typography } from '@mui/material';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import ReactFlow, { Background, Controls, MarkerType, MiniMap } from 'reactflow';

import CohortListView from './CohortListView';

const CohortGraphCanvas = ({
  nodes,
  edges,
  viewMode,
  graphState,
  savedCohorts,
  activeCohortId,
  selectedNodeId,
  isLoadingCohorts,
  isExecuting,
  breakpointNodeId,
  layoutDirection,
  layoutVersion,
  nodeTypes,
  onInit,
  onNodeClick,
  onToggleExpanded,
  onSelectNode,
  onOpenCohort,
  onCreateRootNode,
  onDuplicateCohort,
  onDeleteSavedCohort,
  onLoadDemo,
  onOpenGraphView,
  onEditNode,
  onAddChild,
  onDuplicateNode,
  onDeleteNode,
  getNodeStatusColor,
}) => {
  const defaultEdgeOptions = {
    type: 'smoothstep',
    markerEnd: { type: MarkerType.ArrowClosed },
    style: { strokeWidth: 2 },
  };

  return (
    <Paper
      sx={{
        height: { xs: '56vh', sm: '62vh', md: '70vh' },
        width: '100%',
        overflow: 'hidden',
        position: 'relative',
      }}
    >
      {viewMode === 'list' ? (
        <CohortListView
          cohorts={savedCohorts}
          activeCohortId={activeCohortId}
          isLoading={isLoadingCohorts}
          isBusy={isExecuting}
          onToggleExpanded={onToggleExpanded}
          onOpenCohort={onOpenCohort}
          onCreateCohort={onCreateRootNode}
          onDuplicateCohort={onDuplicateCohort}
          onDeleteCohort={onDeleteSavedCohort}
        />
      ) : nodes.length === 0 ? (
        <Box
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            p: 4,
            textAlign: 'center',
          }}
        >
          <AccountTreeIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 1 }} />
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
            Open or create a saved cohort
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 520 }}>
            Saved cohorts live in the database. Create one from the list-first flow, then use graph view to edit it.
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
            <Button variant="contained" aria-label="Create saved cohort from empty graph state" onClick={onCreateRootNode}>
              Create Cohort
            </Button>
            <Button variant="outlined" aria-label="Load demo graph from empty graph state" onClick={onLoadDemo}>
              Load Demo Graph
            </Button>
          </Stack>
        </Box>
      ) : (
        <ReactFlow
          key={`graph-${layoutDirection}-${layoutVersion}`}
          nodes={nodes}
          edges={edges}
          deleteKeyCode={null}
          nodeTypes={nodeTypes}
          defaultEdgeOptions={defaultEdgeOptions}
          onInit={onInit}
          onNodeClick={(event, node) => {
            if (node?.id) {
              onNodeClick(node.id);
            }
          }}
          fitView
          fitViewOptions={{
            padding: 0.4,
            minZoom: 0.2,
            maxZoom: 0.88,
          }}
          minZoom={0.2}
          maxZoom={2}
          ariaLabel="Cohort graph builder canvas"
        >
          <Background gap={18} size={1} />
          <Controls />
          <MiniMap
            zoomable
            pannable
            nodeStrokeColor={() => '#ed7d31'}
            nodeColor={() => '#ffffff'}
            nodeBorderRadius={6}
          />
        </ReactFlow>
      )}
    </Paper>
  );
};

export default CohortGraphCanvas;
